import { useState, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button, Checkbox, Column, Columns, Link, Spacer, Typography, useToast } from "@aviary";
import { BackButton } from "@aviary/layouts";
import { useFlippers, useSharedGlobalConfig } from "@shared/hooks";
import { useLocation } from "@shared/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils";
import {
  AccountLockedMessage,
  SignInWrapper,
  TwoFactorAuthenticationForm,
  UnauthorizedErrorBox,
} from "@unauthenticated/shared/components";
import { SignInPasswordInput } from "@unauthenticated/shared/components/SignInForm/SignInPasswordInput";
import { useUserSignIn } from "@unauthenticated/shared/data/mutations";
import { l } from "@unauthenticated/shared/locales/i18n";

import * as styles from "./PasswordForm.styles";

interface Props {
  additionalAttributes?: any;
  email: string;
  forgotPasswordLink: string;
  storeId: string;
}

const PasswordForm = ({ additionalAttributes = {}, email, forgotPasswordLink, storeId }: Props) => {
  const { t } = useTranslation();
  const { storePlatform } = useSharedGlobalConfig();
  const [isSessionExpirationFlipperEnabled] = useFlippers("titan_session_expiration");

  const { search } = useLocation();
  const captchaBypassToken = new URLSearchParams(search).get("captchaBypassToken");

  const [password, setPassword] = useState("");
  const [isPasswordDirty, setIsPasswordDirty] = useState(false);
  const [isCredentialsIncorrect, setIsCredentialsIncorrect] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [lockedOut, setLockedOut] = useState(false);
  const { makeToast } = useToast();

  const [signInUser, { loading: isSignInLoading, data }] = useUserSignIn({
    onCompleted: completedData => {
      const { errors, redirectPath } = completedData?.auth?.userSignIn;

      if (
        errors?.message?.includes("credentials") ||
        errors?.message?.match(/Could not find user/i)
      ) {
        setIsCredentialsIncorrect(true);
      }

      if (errors?.message?.match(/Captcha/i)) {
        makeToast("error", t(l.signIn.CaptchaError));
      }

      setAuthError(errors?.message?.includes("authorized"));

      if (redirectPath) {
        window.location.assign(redirectPath);
      }
    },
    onError: ({ message }) => {
      makeToast("error", message);
    },
  });
  const { twoFactorAuthenticationToken, otpMethod } = data?.auth?.userSignIn ?? {};

  const render2FAForm = () => {
    return (
      <TwoFactorAuthenticationForm
        onUserLockedOut={() => setLockedOut(true)}
        userEmail={email}
        otpMethod={otpMethod}
        twoFactorAuthToken={twoFactorAuthenticationToken}
      />
    );
  };

  const renderPasswordForm = () => {
    return (
      <>
        <form noValidate css={styles.form} onSubmit={handleSignInSubmit}>
          <SignInPasswordInput
            value={password}
            isDirty={isPasswordDirty}
            isLoading={false}
            isCredentialsIncorrect={isCredentialsIncorrect}
            onChange={handlePasswordOnChange}
            onBlur={handlePasswordOnBlur}
            required
          />
          <Spacer height="oneHalf" />
          {isSessionExpirationFlipperEnabled && (
            <div css={styles.checkboxWrapper}>
              <Checkbox checked={rememberMe} onChange={() => setRememberMe(!rememberMe)}>
                <Typography>{t(l.signIn.staySignedInFor7Days)}</Typography>
              </Checkbox>
            </div>
          )}
          <Columns isMarginless css={styles.row}>
            <Column isPaddingless columnWidth={6}>
              <Typography css={styles.textLeft}>
                <Link to={forgotPasswordLink} isColor="system">
                  {t(l.signIn.ForgotPassword)}
                </Link>
              </Typography>
            </Column>
            <Column isPaddingless columnWidth={6}>
              <Button isFullWidth type="submit" isLoading={isSignInLoading}>
                {t(l.signIn.SignIn)}
              </Button>
            </Column>
          </Columns>
        </form>
        <Spacer height="double" />
        <BackButton text={t(l.signIn.backToSignIn)} isUnderLined={false} />
      </>
    );
  };

  const handlePasswordOnChange = e => {
    setPassword(e.target.value);
    setIsCredentialsIncorrect(false);
    setIsPasswordDirty(false);
  };

  const handlePasswordOnBlur = () => {
    if (password) {
      setIsPasswordDirty(true);
    }
  };

  const handleSignInSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    setIsPasswordDirty(true);

    const isValid = !!password && !isCredentialsIncorrect;

    if (!isValid) return;

    const userSignIn = (captchaToken: string) => {
      signInUser({
        variables: {
          input: {
            captchaToken,
            attributes: {
              email,
              password,
              rememberMe,
              optional: {
                ...additionalAttributes,
                storePlatform,
                storeId,
              },
            },
          },
        },
      });
    };

    if (captchaBypassToken) {
      userSignIn(captchaBypassToken);
    } else {
      gRecaptchaExecute(captchaToken => {
        userSignIn(captchaToken);
      });
    }
  };

  return (
    <>
      <div css={styles.subtitle}>
        <Typography type="h2" sizeOverride="h4">
          {t(l.signIn.signInAccount)}
        </Typography>
        {!twoFactorAuthenticationToken && <Typography>{t(l.signIn.enterPasswordBelow)}</Typography>}
      </div>
      <SignInWrapper>
        {authError && <UnauthorizedErrorBox />}
        {lockedOut && <AccountLockedMessage />}
        {twoFactorAuthenticationToken ? render2FAForm() : renderPasswordForm()}
      </SignInWrapper>
    </>
  );
};

export { PasswordForm };
